import { useQuery } from '@tanstack/react-query';
import { get } from '../../utils/networking';

enum CacheKey {
  GetInterpretiveReportStatus = 'GetInterpretiveReportStatus',
}

export interface InterpretiveReportStatus {
  surveyId: number;
  status: 'NOT_STARTED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress: number;
  step: string | null;
  error: string | null;
  /** ISO timestamps, as the API sends them */
  startedAt: string | null;
  completedAt: string | null;
}

interface UseGetInterpretiveReportStatusQueryOptions {
  /**
   * Enable polling when report is in progress
   * Default: true
   */
  enablePolling?: boolean;
  /**
   * Polling interval in milliseconds
   * Default: 2000ms (2 seconds)
   */
  pollingInterval?: number;
}

/**
 * Custom hook for polling interpretive report generation status
 *
 * This hook automatically polls the backend while a report is being generated,
 * and stops polling once the report is completed or failed.
 *
 * @param surveyId - The survey ID to check status for
 * @param options - Configuration options
 * @returns TanStack Query result with status data
 *
 * @example
 * ```tsx
 * const { data: status, isLoading } = useGetInterpretiveReportStatusQuery(surveyId);
 *
 * if (status?.status === 'IN_PROGRESS') {
 *   return <ProgressBar progress={status.progress} step={status.step} />;
 * }
 * ```
 */
export const useGetInterpretiveReportStatusQuery = (
  surveyId: number | undefined,
  options: UseGetInterpretiveReportStatusQueryOptions = {}
) => {
  const { enablePolling = true, pollingInterval = 2000 } = options;

  const query = useQuery<InterpretiveReportStatus>({
    queryKey: [CacheKey.GetInterpretiveReportStatus, surveyId],
    queryFn: () => get<InterpretiveReportStatus>(`/api/interpretive-report/survey/${surveyId}/status`),
    enabled: !!surveyId,
    // Poll every 2 seconds while report is being generated
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!enablePolling) return false;
      if (!status) return false;

      // Stop polling only when report is completed or failed
      // Continue polling for NOT_STARTED to catch when generation starts
      if (status === 'COMPLETED' || status === 'FAILED') {
        return false;
      }

      // Continue polling while not started, pending, or in progress
      return pollingInterval;
    },
    // Refetch on window focus if still in progress (not if not started, completed, or failed)
    refetchOnWindowFocus: (query) => {
      const status = query.state.data?.status;
      return status === 'IN_PROGRESS' || status === 'PENDING';
    },
  });

  return query;
};
